import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col, Modal } from 'react-bootstrap';
import { authAPI, checkinsAPI, sitesAPI, reportsAPI} from '../../utils/api';
import axios from 'axios';
import { API_BASE_URL } from '../../config'; // Import API base URL
import { checkGeofence } from '../../utils/gpsUtils';
import { SwitchCamera, Flashlight, CheckCircle } from 'lucide-react';
import { FaMapMarkerAlt, FaSearch, FaList, FaSignInAlt, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import './CheckInOut.css';

// Create axios instance for form data submissions
const api = axios.create({
  baseURL: API_BASE_URL
});

// Request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const CheckInOut = () => {
  const [torchOn, setTorchOn] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [siteSearch, setSiteSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [useDropdown, setUseDropdown] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const timerIntervalRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImages, setCapturedImages] = useState([]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakId, setBreakId] = useState(null);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakEndTime, setBreakEndTime] = useState(null); // new state
  const [breakEndLocation, setBreakEndLocation] = useState(''); // new state
  const [facingMode, setFacingMode] = useState('environment');
  const [showInstructions, setShowInstructions] = useState(false);


  // State for report submission
  const [report, setReport] = useState('');
  const [attachment, setAttachment] = useState(null);

  // Draft saving states
  const [draftSaved, setDraftSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const lastSavedPhotosCount = useRef(0); // Track last saved photo count to prevent duplicate saves

  const trackingIntervalRef = useRef(null);

  // Draft saving functions
  const saveDraft = async (reportContent, attachmentFile = null, images = null) => {
    try {
      setIsSaving(true);

      // Use provided images or fall back to state
      const imagesToSave = images !== null ? images : capturedImages;

      const draftData = {
        report: reportContent,
        timestamp: new Date().toISOString(),
        checkInId: currentSession?.id || null,
        // Save image data URLs
        capturedImages: imagesToSave
      };
      // Debug: log capturedImages count and preview
      console.log('[Draft Save] Saving draft with', imagesToSave?.length || 0, 'photos');

      // Save draft data to localStorage as backup
      localStorage.setItem('reportDraft', JSON.stringify(draftData));

      // If there's an attachment, save it as well
      if (attachmentFile) {
        const reader = new FileReader();
        reader.onload = () => {
          const attachmentData = {
            name: attachmentFile.name,
            type: attachmentFile.type,
            data: reader.result
          };
          localStorage.setItem('reportAttachment', JSON.stringify(attachmentData));
        };
        reader.readAsDataURL(attachmentFile);
      }

      // **Save to backend server immediately (including photos for force check out recovery)**
      if (currentSession?.id && imagesToSave.length > 0) {
        // Only save to backend if the photo count has changed
        // This prevents sending the same photos multiple times
        const photoCountChanged = imagesToSave.length !== lastSavedPhotosCount.current;

        if (!photoCountChanged) {
          console.log('[Draft Save] Photo count unchanged, skipping backend save to prevent duplicates');
        } else {
          try {
            console.log('[Draft Save] Converting', imagesToSave.length, 'images to blobs...');

            // Convert captured images to blobs
            const photosBlobs = await Promise.all(
              imagesToSave.map(async (imageDataUrl, index) => {
                const blob = await fetch(imageDataUrl).then(res => res.blob());
                console.log(`[Draft Save] Photo ${index + 1} blob size:`, blob.size, 'bytes');
                return { blob, filename: `draft-photo-${index + 1}.jpg` };
              })
            );

            // Create FormData for backend
            const formData = new FormData();
            formData.append('checkInId', currentSession.id);
            formData.append('content', reportContent || '');
            formData.append('replaceDraft', 'true'); // Tell backend to REPLACE, not append

            // Add photos (needed for force check-out recovery)
            console.log('[Draft Save] Adding', photosBlobs.length, 'photos to FormData');
            photosBlobs.forEach(({ blob, filename }, index) => {
              formData.append('draftPhotos', blob, filename);
              console.log(`[Draft Save] Added photo ${index + 1}:`, filename, blob.size, 'bytes');
            });

            // Add attachment if present
            if (attachmentFile) {
              formData.append('attachment', attachmentFile);
            }

            // Send to backend
            console.log('[Draft Save] Sending draft to backend...');
            const response = await api.post('/api/reports/draft', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });

            console.log('[Draft Save] Successfully saved draft to backend:', response.data);

            // Update the last saved photo count
            lastSavedPhotosCount.current = imagesToSave.length;
          } catch (backendError) {
            console.error('[Draft Save] Backend save failed:', backendError);
            console.error('[Draft Save] Error details:', backendError.response?.data);
            // Continue even if backend fails - localStorage is our backup
          }
        }
      } else if (currentSession?.id && reportContent) {
        // Save text-only draft even without images
        try {
          const formData = new FormData();
          formData.append('checkInId', currentSession.id);
          formData.append('content', reportContent || '');

          if (attachmentFile) {
            formData.append('attachment', attachmentFile);
          }

          console.log('[Draft Save] Sending text-only draft to backend...');
          const response = await api.post('/api/reports/draft', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          console.log('[Draft Save] Text-only draft saved to backend:', response.data);
        } catch (backendError) {
          console.error('[Draft Save] Backend save failed:', backendError);
        }
      }

      setDraftSaved(true);
      setLastSaved(new Date());
      console.log('[Draft Save] Draft saved successfully');
    } catch (error) {
      console.error('[Draft Save] Failed to save draft:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const loadDraft = useCallback(() => {
    // This is now handled in the main useEffect
    console.log('Draft loading initiated');
  }, []);

  const clearDraft = () => {
    localStorage.removeItem('reportDraft');
    localStorage.removeItem('reportAttachment');
    setDraftSaved(false);
    setLastSaved(null);
    setReport('');
    setAttachment(null);
    setCapturedImages([]);
  };

  const handleReportChange = (value) => {
    // Get the new report value
    const newReport = typeof value === 'function' ? value(report) : value;

    // Update the state
    setReport(newReport);
    setDraftSaved(false);

    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (1 second after user stops typing)
    autoSaveTimeoutRef.current = setTimeout(() => {
      // Use the latest state values when saving, including captured images
      saveDraft(newReport, attachment, capturedImages);
    }, 1000);
  };

  const enableTorch = async (on) => {
    if (!streamRef.current) return;

    const videoTrack = streamRef.current.getVideoTracks()[0];
    const capabilities = videoTrack.getCapabilities();

    if (!capabilities.torch) {
      console.warn('Torch not supported on this device');
      return;
    }

    try {
      await videoTrack.applyConstraints({ advanced: [{ torch: on }] });
      setTorchOn(on);
    } catch (err) {
      console.error('Failed to toggle torch:', err);
    }
  };


  const handleAttachmentChange = (file) => {
    setAttachment(file);
    setDraftSaved(false);

    // Auto-save when attachment changes
    if (report.trim()) {
      saveDraft(report, file, capturedImages);
    }
  };

  const startBreak = async () => {
    try {
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject)
      );
      const { latitude, longitude } = position.coords;

      const response = await api.post(`/api/breaks/start`, {
        checkInId: currentSession.id,
        latitude,
        longitude
      });

      setBreakId(response.data.id);
      setIsOnBreak(true);
      setBreakStartTime(new Date());
    } catch (error) {
      console.error("Start break location error:", error);
      setError("Failed to retrieve location. Please ensure location is enabled.");
    }
  };


  const endBreak = async () => {
    try {
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject)
      );
      const { latitude, longitude } = position.coords;

      await api.post(`/api/breaks/end/${breakId}`, {
        latitude,
        longitude
      });

      const address = await getAddressFromCoords(latitude, longitude);

      setBreakId(null);
      setIsOnBreak(false);
      setBreakEndTime(new Date());
      setBreakEndLocation(address);
    } catch (error) {
      console.error("End break location error:", error);
      setError("Failed to retrieve location. Please ensure location is enabled.");
    }
  };






  const startTimer = useCallback((startTime) => {
    if (timerIntervalRef.current) {
      console.log('[Timer] Clearing old interval:', timerIntervalRef.current);
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    const interval = setInterval(() => {
      const now = new Date();
      const diff = now - new Date(startTime);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setElapsedTime(
        `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    }, 1000);

    timerIntervalRef.current = interval;
    console.log('[Timer] Started new interval:', interval);
  }, []);




  // Fetch available sites and check if user is already checked in
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sitesResponse, profileResponse] = await Promise.all([
          sitesAPI.getAssigned(),
          authAPI.getProfile()
        ]);

        const assignedSites = sitesResponse.data || [];

        // Backend now properly filters sites by region, so we can use them directly
        setAvailableSites(assignedSites);

        const checkInResponse = await checkinsAPI.getActive();

        if (checkInResponse.data?.active && checkInResponse.data.checkIn?.id) {
          const checkInData = checkInResponse.data.checkIn;

          // Format location if coordinates exist
          let location = '';
          if (checkInData.latitude && checkInData.longitude) {
            location = `Lat: ${parseFloat(checkInData.latitude).toFixed(4)}, Long: ${parseFloat(checkInData.longitude).toFixed(4)}`;
          }

          setIsCheckedIn(true);
          setCurrentSession({
            id: checkInData.id,
            siteId: checkInData.siteId,
            siteName: checkInData.site?.name || 'Unknown Site',
            checkInTime: checkInData.checkInTime,
            location // ✅ Correctly assigned
          });

          startTimer(new Date(checkInData.checkInTime));

          // Load draft for this session
          loadDraft();
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please refresh the page.');
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      // Clear auto-save timeout on unmount
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [loadDraft]);


  // Start location tracking every 5 minutes while checked in
  useEffect(() => {
    const sendLocation = async () => {
      try {
        const position = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject)
        );

        const { latitude, longitude } = position.coords;

        // 🔍 Reverse geocode to get a readable address
        const location = await getAddressFromCoords(latitude, longitude);

        console.log("Tracking payload:", { latitude, longitude, location });

        // 📤 Send to backend
        await api.post('/api/tracking/track', {
          checkInId: currentSession.id,
          latitude,
          longitude,
          location
        });

        console.log("📍 Location tracked successfully");
      } catch (err) {
        console.warn("⚠️ Failed to track location:", err);
      }
    };


    if (isCheckedIn && currentSession?.id) {
      sendLocation(); // Send one immediately
      trackingIntervalRef.current = setInterval(sendLocation, 5 * 60 * 1000); // Every 5 minutes
    }

    return () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };
  }, [isCheckedIn, currentSession?.id]);



  const getAddressFromCoords = async (latitude, longitude) => {
    const API_KEY = "AIzaSyCm_5BujVh9fZNy10-gJgHq2rTblBfyjw0";
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${API_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK" && data.results.length > 0) {
        return data.results[0].formatted_address;
      }
      return '';
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return '';
    }
  };


  // Handle check in
  const handleCheckIn = () => {
    if (!selectedSite) {
      setError("Please select a site to check in to.");
      return;
    }

    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }

    setError("");
    setSuccess("");
    setIsSubmitting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const locationName = await getAddressFromCoords(latitude, longitude);

        try {
          // Find the selected site to get geofence information
          const site = availableSites.find(s => s.id.toString() === selectedSite);

          if (site && site.latitude && site.longitude && site.geofenceRadius) {
            // Validate coordinate ranges
            const siteLat = parseFloat(site.latitude);
            const siteLng = parseFloat(site.longitude);
            
            if (isNaN(siteLat) || isNaN(siteLng) || siteLat < -90 || siteLat > 90 || siteLng < -180 || siteLng > 180) {
              setError(`Site coordinates are invalid. Please contact your administrator to fix site "${site.name}" coordinates.`);
              console.error('[Check-In Error] Invalid site coordinates:', { 
                siteName: site.name, 
                latitude: site.latitude, 
                longitude: site.longitude 
              });
              return;
            }
            
            if (isNaN(latitude) || isNaN(longitude) || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
              setError('Your GPS location is invalid. Please ensure location services are enabled and try again.');
              console.error('[Check-In Error] Invalid user coordinates:', { latitude, longitude });
              return;
            }
            
            // Debug logging for coordinate validation
            console.log('[Check-In Debug] User Location:', { latitude, longitude, accuracy });
            console.log('[Check-In Debug] Site Location:', { 
              latitude: siteLat, 
              longitude: siteLng,
              geofenceRadius: site.geofenceRadius,
              siteName: site.name 
            });
            
            // Add accuracy buffer to account for GPS variance
            // Backend may use strict validation, so we add buffer here
            const accuracyBuffer = Math.max(accuracy || 0, 10); // Minimum 10m buffer
            const effectiveRadius = site.geofenceRadius + accuracyBuffer;
            
            console.log('[Check-In Debug] Effective Radius:', { 
              configuredRadius: site.geofenceRadius, 
              accuracyBuffer, 
              effectiveRadius 
            });
            
            // Check if user is within geofence radius (with accuracy buffer)
            const geofenceCheck = checkGeofence(
              latitude,
              longitude,
              siteLat,
              siteLng,
              effectiveRadius
            );

            console.log('[Check-In Debug] Geofence Result:', geofenceCheck);

            if (!geofenceCheck.isWithinRadius) {
              setError(`You are ${geofenceCheck.distance.toFixed(1)}m away from the site. You must be within ${site.geofenceRadius}m (±${accuracyBuffer}m GPS buffer) to check in. Please move closer or wait for better GPS accuracy.`);
              return;
            }

            // Check location accuracy based on site requirements
            if (site.locationAccuracy === 'high' && accuracy > 10) {
              setError(`Location accuracy is ${accuracy.toFixed(1)}m. High accuracy (≤10m) is required for this site. Please move to an open area and try again.`);
              return;
            } else if (site.locationAccuracy === 'medium' && accuracy > 50) {
              setError(`Location accuracy is ${accuracy.toFixed(1)}m. Medium accuracy (≤50m) is required for this site. Please try again.`);
              return;
            }

            setSuccess(`Location verified! You are ${geofenceCheck.distance.toFixed(1)}m from the site center (±${accuracy.toFixed(1)}m accuracy).`);
          }

          const response = await checkinsAPI.checkIn(selectedSite, {
            latitude,
            longitude,
            locationName // now use the human-readable address instead of raw coords
          });

          if (response.data && response.data.checkIn) {
            const checkInData = response.data.checkIn;

            await api.post("/api/tracking/track", {
              checkInId: checkInData.id,
              latitude,
              longitude,
              location: locationName
            });


            // ✅ FIX 1: Clear all old draft data before setting new session
            // This prevents old drafts from appearing in the new check-in
            console.log('[Check-In] Clearing all old draft data for new session');
            localStorage.removeItem('reportDraft');
            localStorage.removeItem('reportAttachment');
            setReport('');
            setAttachment(null);
            setCapturedImages([]);
            setDraftSaved(false);
            setLastSaved(null);
            setElapsedTime('00:00:00'); // ✅ Reset elapsed time display
            hasLoadedDraft.current = null;
            lastSavedPhotosCount.current = 0;

            setIsCheckedIn(true);
            setCurrentSession({
              id: checkInData.id,
              siteId: checkInData.siteId,
              siteName: availableSites.find(site => site.id === checkInData.siteId)?.name || "Unknown Site",
              checkInTime: checkInData.checkInTime,
              locationName // use the variable, not the global location
            });
            setSuccess("You have successfully checked in!");

            setTimeout(() => setSuccess(""), 3000);
            startTimer(new Date(checkInData.checkInTime));
            setIsSubmitting(false);

            // Load any existing draft for this session (should be empty for new check-in)
            loadDraft();
          }
        } catch (error) {
          console.error("Check-in failed:", error);
          setError(error.response?.data?.message || error.message || "Failed to check in. Please try again.");
          setIsSubmitting(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setError("Location access is required for check-in.");
        setIsSubmitting(false);
      }
    );
  };




  // Handle check out and report submission
  const handleCheckOut = async () => {
    if (!report.trim()) {
      setError("Please enter a report before checking out.");
      return;
    }
    if (capturedImages.length === 0) {
      setError("Please capture at least one check-out photo before checking out.");
      return;
    }
    if (isOnBreak) {
      setError("You must end your break before checking out.");
      return;
    }

    // Get current location for check-out validation
    if (!navigator.geolocation) {
      setError("Geolocation is required for check-out validation.");
      return;
    }

    try {
      setError("");
      setSuccess("");
      setLoading(true);
      setIsSubmitting(true);
      if (!currentSession || !currentSession.id) {
        throw new Error('No active check-in session found');
      }

      // Get current location
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject)
      );
      const { latitude, longitude } = position.coords;
      let { accuracy } = position.coords;
      // Ensure accuracy is a number, rounded to 3 decimals, clamped to 99999.999
      accuracy = typeof accuracy === 'number' ? Math.min(99999.999, Math.round(accuracy * 1000) / 1000) : 0;

      // Check out with location data
      await checkinsAPI.checkOut({
        latitude,
        longitude,
        accuracy
      });
      const formData = new FormData();
      formData.append('checkInId', currentSession.id);
      formData.append('content', report);

      // Add flag to indicate we're using draft photos (don't duplicate them)
      formData.append('useDraftPhotos', 'true');

      if (attachment) {
        formData.append('file', attachment, attachment.name || 'attachment.jpg');
      }

      // Still send the photos for backward compatibility, but backend should check 'useDraftPhotos' flag
      // and use the draft photos instead of these to avoid duplication
      for (let i = 0; i < capturedImages.length; i++) {
        // ✅ FIX: Explicitly create blob with correct MIME type to prevent upload errors
        const response = await fetch(capturedImages[i]);
        let blob = await response.blob();

        // Ensure blob has correct MIME type (sometimes fetch loses it)
        if (!blob.type || blob.type === '') {
          blob = new Blob([blob], { type: 'image/jpeg' });
        }

        formData.append('checkOutPhotos', blob, `photo-${i + 1}.jpg`);
        console.log(`Photo ${i + 1} - size: ${blob.size} bytes, type: ${blob.type}`);
      }
      const response = await api.post('/api/reports', formData);
      console.log('Report submission response:', response.data);

      // ✅ FIX: Stop the timer interval to prevent it from continuing
      if (timerIntervalRef.current) {
        console.log('[Check-Out] Clearing timer interval:', timerIntervalRef.current);
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      setIsCheckedIn(false);
      setCurrentSession(null);
      setElapsedTime("00:00:00");
      setReport("");
      setAttachment(null);
      setCapturedImages([]);
      setSuccess("Check-out successful!");
      setShowSuccessModal(true);
      // Clear draft after successful submission
      clearDraft();
      // Reset the draft loaded flag so it can load fresh for next session
      hasLoadedDraft.current = null;
      // Reset photo count tracker
      lastSavedPhotosCount.current = 0;
    } catch (error) {
      console.error("Check-out error:", error);
      const errorMsg = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Check-out failed";
      setError(errorMsg);
      if (error.response) {
        console.error("Server response:", error.response.data);
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleAttachmentChange(e.target.files[0]);
    }
  };

  // Update the captured images state and save draft
  useEffect(() => {
    // Auto-save draft when capturedImages changes
    if (report.trim() || attachment || capturedImages.length > 0) {
      // Pass the current capturedImages explicitly to ensure they're saved
      console.log('[UseEffect] capturedImages changed, saving draft with', capturedImages.length, 'photos');
      saveDraft(report, attachment, capturedImages);

      // Force update the input value to prevent cursor jumping
      const textarea = document.querySelector('textarea[aria-label="Report"]');
      if (textarea && textarea.value !== report) {
        textarea.value = report;
      }
    }
    // We only want to run this effect when capturedImages changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedImages]);

  // Track if we've loaded the initial draft
  const hasLoadedDraft = useRef(null);
  const previousSessionId = useRef(null);

  // Load draft when component mounts or session changes
  useEffect(() => {
    const loadDraftData = async () => {
      // Reset the flag when session changes - this allows loading draft for new sessions
      // But skip if we've already loaded a draft for THIS SPECIFIC session
      const currentSessionId = currentSession?.id;
      if (hasLoadedDraft.current === currentSessionId) {
        console.log('[Draft Load] Already loaded draft for session:', currentSessionId);
        return;
      }

      try {
        // Clear any existing timeouts
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }

        // ONLY clear data when session ID actually CHANGES (not on initial load or page refresh)
        // This prevents clearing data on page reload
        if (currentSessionId && previousSessionId.current && previousSessionId.current !== currentSessionId) {
          // ✅ FIX 3: Clear BOTH localStorage AND React state when session changes
          console.log('[Draft Load] Session changed from', previousSessionId.current, 'to', currentSessionId, '- clearing ALL data');

          // Clear localStorage to prevent old drafts from loading
          localStorage.removeItem('reportDraft');
          localStorage.removeItem('reportAttachment');

          // Clear React state
          setReport('');
          setAttachment(null);
          setCapturedImages([]);
          setDraftSaved(false);
          setLastSaved(null);
          lastSavedPhotosCount.current = 0; // Reset photo count tracker for new session
        }

        // Update the previous session ID tracker
        previousSessionId.current = currentSessionId;

        // **NEW: Try loading from backend first if we have a session**
        if (currentSession?.id) {
          try {
            const backendResponse = await api.get(`/api/reports/draft/${currentSession.id}`);

            if (backendResponse.data && backendResponse.data.draft) {
              const backendDraft = backendResponse.data.draft;

              // Set the flag to the current session ID to prevent re-loading for this session
              hasLoadedDraft.current = currentSessionId;

              // Load report content
              setReport(backendDraft.content || '');

              // Load photos from backend URLs
              if (backendDraft.photos && Array.isArray(backendDraft.photos)) {
                // Convert backend photo URLs to data URLs for display
                const photoUrls = backendDraft.photos.map(photo =>
                  `${API_BASE_URL}${photo.url || photo.path}`
                );
                setCapturedImages(photoUrls);
              }

              // Load attachment info if exists
              if (backendDraft.attachmentPath) {
                // Note: We can't recreate the File object from the server,
                // but we can show that an attachment exists
                console.log('[Draft Load] Attachment exists:', backendDraft.attachmentPath);
              }

              setLastSaved(new Date(backendDraft.updatedAt || backendDraft.timestamp));
              setDraftSaved(true);

              console.log('[Draft Load] Loaded from backend successfully');
              return; // Exit early, we loaded from backend
            }
          } catch (backendError) {
            console.warn('[Draft Load] Backend load failed, trying localStorage:', backendError);
            // Fall through to localStorage loading
          }
        }

        // Fallback: Get the draft data from localStorage
        const draftData = localStorage.getItem('reportDraft');
        if (draftData) {
          const parsed = JSON.parse(draftData);

          // ✅ FIX 2: STRICT validation - only load if checkInId EXACTLY matches
          // Changed from: if (!currentSession || parsed.checkInId === currentSession.id)
          // The old condition allowed loading when !currentSession, which could load wrong drafts
          if (currentSession && parsed.checkInId === currentSession.id) {
            // Set the flag to the current session ID to prevent re-loading for this session
            hasLoadedDraft.current = currentSessionId;

            // Update all states in a single batch
            setReport(parsed.report || '');

            // Load captured images if they exist
            if (parsed.capturedImages && Array.isArray(parsed.capturedImages)) {
              setCapturedImages([...parsed.capturedImages]);
            }

            // Load attachment if exists
            const attachmentData = localStorage.getItem('reportAttachment');
            if (attachmentData) {
              const parsedAttachment = JSON.parse(attachmentData);
              fetch(parsedAttachment.data)
                .then(res => res.blob())
                .then(blob => {
                  const file = new File([blob], parsedAttachment.name, { type: parsedAttachment.type });
                  setAttachment(file);
                });
            }

            setLastSaved(new Date(parsed.timestamp));
            setDraftSaved(true);

            console.log('[Draft Load] Loaded from localStorage');
          }
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    };

    loadDraftData();

    // Cleanup function
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
    // We only want to run this effect when currentSession?.id changes
  }, [currentSession?.id]);

  // Handle camera photo capture
  const handleCapturePhoto = () => {
    // Check if maximum photos limit is reached
    if (capturedImages.length >= 4) {
      setError('Maximum of 4 photos allowed');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageDataUrl = canvas.toDataURL('image/jpeg');

    console.log('[Capture Photo] New photo captured, size:', imageDataUrl.length, 'chars');

    // Update state - the useEffect will handle saving the draft
    setCapturedImages(prev => {
      const updatedImages = [...prev, imageDataUrl];
      console.log('[Capture Photo] Total images now:', updatedImages.length);
      return updatedImages;
    });
  };
  const handleSwitchCamera = async () => {
    if (!streamRef.current) return;

    // Stop current stream
    streamRef.current.getTracks().forEach(track => track.stop());

    // Switch facing mode
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');

    // Re-open camera with new facing mode
    await handleOpenCamera();
  };


  // Handle retaking photo
  const handleRetakePhoto = () => {
    setCapturedImages([]);
    handleOpenCamera();
  };

  // Handle opening camera
  const handleOpenCamera = async () => {
    setIsCameraOpen(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    try {
      const constraints = {
        video: {
          facingMode, // front or rear
          width: { ideal: 1280 },
          height: { ideal: 720 },
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      videoRef.current.srcObject = stream;
      streamRef.current = stream;

      // Try enabling torch if supported
      if (torchOn) await enableTorch(true);

      await new Promise(resolve => videoRef.current.onloadedmetadata = resolve);
    } catch (err) {
      console.error('Camera error:', err);
      setError(err.message || 'Camera access failed');
      setIsCameraOpen(false);
    }
  };

  // Enhanced cleanup
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <h1 className="mb-2 mb-md-4">Check In/Out</h1>
        </Col>
      </Row>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <div className="mb-3">
                  <CheckCircle size={64} className="text-success" />
                </div>
                <h4 className="mb-3">Check-out Successful!</h4>
                <p>You have been successfully checked out.</p>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccess('');
                  }}
                  className="mt-3 px-4"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Row>
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header as="h5" className="bg-primary text-white d-flex justify-content-between align-items-center">
              <span>{isCheckedIn ? "Check Out & Submit Report" : "Check In"}</span>
              {isCheckedIn && (
                <Button
                  variant="link"
                  className="help-button-header"
                  onClick={() => setShowInstructions(true)}
                  title="Help & Instructions"
                >
                  <FaQuestionCircle size={20} />
                </Button>
              )}
            </Card.Header>
            <Card.Body>
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center p-4">
                    <Spinner animation="border" role="status" className="mb-2">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <span className="small text-muted">Loading...</span>
                  </div>
                ) : (
                  <>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                  {!isCheckedIn ? (
                    <div className="check-in-form">
                      <div className="check-in-header">
                        <div className="header-icon-wrapper">
                          <FaMapMarkerAlt className="header-icon" />
                        </div>
                        <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                          <h2 className="check-in-title mb-0">Check In to Site</h2>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="help-button-checkin"
                          onClick={() => setShowInstructions(true)}
                        >
                          <FaQuestionCircle /> <span className="help-text">How to use</span>
                        </Button>
                        <p className="check-in-subtitle">Select your work location to begin</p>
                      </div>                      <Form>
                        <div className="site-mode-selector">
                          <label className="mode-label">Site Selection Mode:</label>
                          <div className="toggle-container">
                            <button
                              type="button"
                              className={`toggle-option ${!useDropdown ? 'active' : ''}`}
                              onClick={() => {
                                setUseDropdown(false);
                                setSiteSearch("");
                                setSelectedSite("");
                                setShowDropdown(false);
                              }}
                            >
                              <FaSearch className="toggle-icon" /> Type/Search
                            </button>
                            <button
                              type="button"
                              className={`toggle-option ${useDropdown ? 'active' : ''}`}
                              onClick={() => {
                                setUseDropdown(true);
                                setSiteSearch("");
                                setSelectedSite("");
                                setShowDropdown(false);
                              }}
                            >
                              <FaList className="toggle-icon" /> Dropdown
                            </button>
                          </div>
                        </div>

                        {!useDropdown ? (
                          <Form.Group controlId="siteSearch" className="mb-4 site-search-container">
                            <Form.Label className="search-label">Type Site Name</Form.Label>
                            <div className="search-input-wrapper">
                              <FaSearch className="search-input-icon" />
                              <Form.Control
                                type="text"
                                className="site-search-input"
                                placeholder="Start typing site name..."
                                value={siteSearch}
                                onChange={e => {
                                  setSiteSearch(e.target.value);
                                  setSelectedSite("");
                                  setShowDropdown(true);
                                }}
                                autoComplete="off"
                                onFocus={() => siteSearch && setShowDropdown(true)}
                              />
                            </div>
                            {siteSearch && showDropdown && (
                              <div className="autocomplete-dropdown">
                                {availableSites
                                  .filter(site => site.name.toLowerCase().includes(siteSearch.toLowerCase()))
                                  .map((site, index) => (
                                    <div
                                      key={site.id}
                                      className="autocomplete-item"
                                      onClick={() => {
                                        setSelectedSite(site.id.toString());
                                        setSiteSearch(site.name);
                                        setShowDropdown(false);
                                      }}
                                    >
                                      <FaMapMarkerAlt className="item-icon" />
                                      {index + 1}. {site.name}
                                    </div>
                                  ))}
                                {availableSites.filter(site => site.name.toLowerCase().includes(siteSearch.toLowerCase())).length === 0 && (
                                  <div className="autocomplete-item autocomplete-no-results">No sites found</div>
                                )}
                              </div>
                            )}
                          </Form.Group>
                        ) : (
                          <Form.Group controlId="siteDropdown" className="mb-4">
                            <Form.Label className="search-label">Select Site</Form.Label>
                            <select
                              className="form-select site-select-input"
                              value={selectedSite}
                              onChange={e => setSelectedSite(e.target.value)}
                            >
                              <option value="">Select a site...</option>
                              {availableSites.map((site, index) => (
                                <option key={site.id} value={site.id}>{index + 1}. {site.name}</option>
                              ))}
                            </select>
                          </Form.Group>
                        )}

                        <div className="d-flex justify-content-center mt-2">
                          <Button
                            variant="primary"
                            onClick={handleCheckIn}
                            className="btn-check-in-primary"
                            disabled={!selectedSite || isSubmitting}
                          >
                            {isSubmitting ? (
                              <><Spinner as="span" animation="border" size="sm" className="me-2" />Checking in…</>
                            ) : (
                              <><FaSignInAlt className="btn-icon" /> Check In Now</>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  ) : (
                    <div className="check-out-form">
                      <Row>
                        <Col xs={12}>
                          <div className="session-info mb-4">
                            <h4 className="mb-3">Current Session</h4>
                            <Row>
                              <Col xs={12} md={6}>
                                <p>
                                  <strong>Site:</strong> {currentSession?.siteName || 'Loading...'}
                                </p>
                                <p className="mb-0">
                                  <strong>Check-in Time:</strong>{' '}
                                  {currentSession?.checkInTime ? new Date(currentSession.checkInTime).toLocaleString() : 'Not checked in'}
                                </p>
                              </Col>
                              <Col xs={12} md={6}>
                                <p className="mb-0">
                                  <strong>Elapsed Time:</strong> {elapsedTime}
                                </p>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={12}>
                          <div className="status-section mb-4">
                            <h5 className="mb-3">
                              Status: <span className={isOnBreak ? 'text-warning' : 'text-success'}>
                                {isOnBreak ? 'On Break' : 'Working'}
                              </span>
                            </h5>

                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                              {!isOnBreak ? (
                                <Button variant="warning" onClick={startBreak} className="flex-grow-1" style={{ maxWidth: '300px' }}>
                                  Start Break
                                </Button>
                              ) : (
                                <Button variant="success" onClick={endBreak} className="flex-grow-1" style={{ maxWidth: '300px' }}>
                                  End Break
                                </Button>
                              )}

                              <div className="break-info">
                                {breakStartTime && (
                                  <p className="mb-1 small">
                                    <strong>Break Started:</strong>{' '}
                                    {new Date(breakStartTime).toLocaleTimeString()}
                                  </p>
                                )}

                                {breakEndTime && (
                                  <p className="mb-1 small">
                                    <strong>Break Ended:</strong>{' '}
                                    {new Date(breakEndTime).toLocaleTimeString()}
                                  </p>
                                )}

                                {breakEndLocation && (
                                  <p className="mb-0 small">
                                    <strong>Break End Location:</strong> {breakEndLocation}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={12}>
                          <div className="report-form-section">
                            <h4 className="mb-3">Submit Report</h4>
                            <Form>
                              <Form.Group controlId="reportContent" className="mb-3">
                                <Form.Label>Report Content</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={5}
                                  value={report}
                                  onChange={(e) => handleReportChange(e.target.value)}
                                  placeholder="Enter your report here..."
                                />
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                  <div className="draft-status">
                                    {isSaving && (
                                      <small className="text-muted">
                                        <Spinner size="sm" className="me-1" />
                                        Saving draft...
                                      </small>
                                    )}
                                    {draftSaved && !isSaving && lastSaved && (
                                      <small className="text-success">
                                        ✓ Draft saved at {lastSaved.toLocaleTimeString()}
                                      </small>
                                    )}
                                    {!draftSaved && !isSaving && report.trim() && (
                                      <small className="text-warning">
                                        ⚠ Unsaved changes
                                      </small>
                                    )}
                                  </div>
                                  <div className="draft-actions">
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => saveDraft(report, attachment, capturedImages)}
                                  disabled={!report.trim() || isSaving}
                                  className="me-2"
                                >
                                  {isSaving ? 'Saving...' : 'Save Draft'}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={clearDraft}
                                  disabled={!report.trim() && !attachment}
                                >
                                  Clear Draft
                                </Button>
                              </div>
                            </div>
                          </Form.Group>

                          {/* Camera Feature */}
                          <Form.Group controlId="cameraInput" className="mb-3">
                            <Form.Label>
                              Take Photo (Required) - {capturedImages.length}/4 photos
                            </Form.Label>
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-wrap align-items-center gap-4 mb-2">
                                <Button
                                  variant="outline-primary"
                                  onClick={() => {
                                    setIsCameraOpen(true);
                                    setTimeout(handleOpenCamera, 50);
                                  }}
                                  className="flex-grow-1 flex-md-grow-0"
                                  disabled={isCameraOpen || !navigator.mediaDevices?.getUserMedia || capturedImages.length >= 4}
                                >
                                  {isCameraOpen ? 'Camera Loading...' : 'Open Camera'}
                                </Button>

                                {/* Camera Controls Container */}
                                <div className="d-flex gap-4">
                                  {/* Torch Toggle */}
                                  <Button
                                    variant={torchOn ? "warning" : "primary"}
                                    onClick={() => enableTorch(!torchOn)}
                                    className="p-6 rounded-full"
                                    title={torchOn ? "Turn Torch Off" : "Turn Torch On"}
                                    disabled={!isCameraOpen}
                                  >
                                    <Flashlight className="h-5 w-5" />
                                  </Button>

                                  {/* Switch Camera */}
                                  <Button
                                    variant="primary"
                                    onClick={handleSwitchCamera}
                                    className="p-6 rounded-full"
                                    title="Switch Camera"
                                    disabled={!isCameraOpen}
                                  >
                                    <SwitchCamera className="h-5 w-5" />
                                  </Button>
                                </div>
                              </div>

                              {capturedImages.length > 0 && (
                                <div className="w-100">
                                  <Button
                                    variant="outline-danger"
                                    onClick={handleRetakePhoto}
                                    className="w-100 w-md-auto"
                                  >
                                    Retake
                                  </Button>
                                </div>
                              )}

                              {isCameraOpen && (
                                <div className="camera-preview mb-2 w-100">
                                  <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className="img-fluid rounded w-100"
                                  />
                                  <Button
                                    variant="primary"
                                    onClick={handleCapturePhoto}
                                    className="mt-2 w-100 w-md-auto"
                                    disabled={capturedImages.length >= 4}
                                  >
                                    {capturedImages.length >= 4 ? 'Maximum Photos Reached' : 'Capture Photo'}
                                  </Button>
                                </div>
                              )}

                              {capturedImages.length > 0 && (
                                <div className="captured-photo-preview mt-2 w-100">
                                  <h6>Captured Photos ({capturedImages.length}/4)</h6>
                                  <div className="d-flex flex-wrap">
                                    {capturedImages.map((img, idx) => (
                                      <div key={idx} className="position-relative m-1">
                                        <img
                                          src={img}
                                          alt={`Captured ${idx + 1}`}
                                          className="img-thumbnail"
                                          style={{ width: '150px', height: 'auto' }}
                                        />
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          className="position-absolute top-0 end-0 m-1"
                                          onClick={() => {
                                            setCapturedImages(prev => prev.filter((_, i) => i !== idx));
                                          }}
                                          style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
                                        >
                                          ×
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Form.Group>



                          {/* Existing Attachment Feature */}
                          <Form.Group controlId="reportAttachment" className="mb-3">
                            <Form.Label>Attachment (Optional)</Form.Label>
                            <Form.Control
                              type="file"
                              onChange={handleFileChange}
                            />
                          </Form.Group>

                          <div className="d-flex justify-content-center">
                            <Button
                              variant="danger"
                              onClick={handleCheckOut}
                              className="w-100 w-md-auto"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <><Spinner as="span" animation="border" size="sm" className="me-2" />Checking out…</>
                              ) : (
                                'Submit Report & Check Out'
                              )}
                            </Button>
                          </div>
                        </Form>
                      </div>
                    </Col>
                  </Row>
                </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Instructions Modal */}
      <Modal show={showInstructions} onHide={() => setShowInstructions(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Instructions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small">
            <p><strong>Check In:</strong></p>
            <ul>
              <li>Select your assigned site</li>
              <li>Ensure location services are enabled</li>
              <li>You must be within 10m of the site</li>
            </ul>

            <p><strong>Check Out:</strong></p>
            <ul>
              <li>Complete your work report</li>
              <li>Take required photos (max 4)</li>
              <li>End any active breaks first</li>
              <li>Your report is auto-saved as you type</li>
              <li>Use "Save Draft" to manually save</li>
            </ul>

            <p><strong>Breaks:</strong></p>
            <ul>
              <li>Start/end breaks as needed</li>
              <li>Location is tracked for breaks</li>
              <li>Must end breaks before checkout</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInstructions(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CheckInOut;
